<script lang="ts" setup>
import { TaskViewElementEnum } from '@/components/tasks-kanban/enums';
import { TaskModel } from '@/components/tasks-kanban/models';
import { TitledLayout } from '@/layouts';
import { PaginationRequestModel, PaginationResponseModel, ProjectModel, UserModel } from '@/models';
import { computed, onMounted, provide, ref } from 'vue';
import { useRouter } from 'vue-router';
import { TaskSortingEnum } from './enums';
import { AdapterService } from './adapter';
import {
  GetProjectTasksRequestModel,
  TaskColumnModel,
  TaskFilterModel,
  TaskSortingModel,
} from './models';
import { TaskFilterModel as TaskKanbanFilterModel } from '@/components/tasks-kanban/models';
import { TasksKanbanFilter } from '@/components/tasks-kanban/components';
import { debounce } from 'tir-pm-debounce';
import { TirPmScrollbar, TirPmPagination, TirPmIconView } from 'tir-components';
import { TaskHeader, TaskCard } from './components';

/**Адаптер */
const adapter = new AdapterService();
/**Роутер */
const router = useRouter();

// #region defineProps
const props = defineProps({
  /**ID проекта */
  projectId: {
    type: Number,
    required: true,
  },
  /** ID сотрудника */
  employeeId: {
    type: Number,
    required: true,
  },
});
// #endregion defineProps

// #region Переменные
/**Колонки */
const columns: TaskColumnModel[] = [
  new TaskColumnModel({
    Type: TaskViewElementEnum.Namespace,
    Name: '',
    Width: '1.5rem',
    MinWidth: '1.5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Queue,
    Name: 'Очередь',
    Width: '9.375rem',
    MinWidth: '9.375rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Number,
    Name: '№',
    Width: '5rem',
    MinWidth: '5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Name,
    Name: 'Название',
    Width: '50vw',
    MinWidth: '20rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Status,
    Name: 'Статус',
    Width: '5.1875rem',
    MinWidth: '5.1875rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Priority,
    Name: 'Приоритет',
    Width: '4rem',
    MinWidth: '4rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Difficulty,
    Name: 'Сложность',
    Width: '2rem',
    MinWidth: '2rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.EstimateTime,
    Name: 't (оценка)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.WorkTime,
    Name: 't (затрачено)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
];
/**Дефолтное кол-во элементов на странице */
const DEFAULT_PAGE_STEP = 10;
/**ID неймспейса */
let namespaceId: string | undefined;
/**Роль сотрудника в задачах */
let role: 'observer' | 'performer';
// #endregion Переменные

// #region refs
/**Загрузка при инициализации */
const isInitLoading = ref(false);
/**Проект */
const project = ref<ProjectModel>();
/**Сотрудник */
const employee = ref<UserModel>();
/**Данные сортировки */
const sortingData = ref(
  new TaskSortingModel({
    OrderById: true,
  }),
);
/**Загрузка при сортировке */
const isLoadingSort = ref(false);
/**Кол-во элементов на странице */
const pageStep = ref(DEFAULT_PAGE_STEP);
/**Страница */
const pageResponse = ref<PaginationResponseModel<Array<TaskModel>>>();
/**Фильтр */
const filter = ref(new TaskFilterModel());
/**Настройки видимости */
const viewSettings = ref([
  TaskViewElementEnum.Namespace,
  TaskViewElementEnum.Queue,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.Difficulty,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
]);

provide('popover-scroll-selector', '.project-list-wrapper-content');
// #endregion refs

// #region computed
/**Список задач */
const tasks = computed(() => pageResponse.value?.Items ?? []);
/**Кол-во страниц */
const pagesCount = computed(
  () => pageResponse.value && Math.ceil(pageResponse.value.Count / pageResponse.value.Size),
);
/**Текущая страница */
const currentPageRequest = computed(() => {
  if (pageResponse.value)
    return new PaginationRequestModel({
      Skip: pageResponse.value.Size * (pageResponse.value.Current - 1),
      Take: pageStep.value,
    });
});
// #endregion computed

// #region Хуки жизненного цикла
onMounted(() => {
  getQueryParams();
  setDefaultFilter();
  getData();
});
// #endregion Хуки жизненного цикла

// #region Функции
/**Установить фильтр по дефолту */
const setDefaultFilter = () => {
  if (namespaceId) {
    filter.value.Namespaces = [namespaceId];
  }
};
/**Получить сотрудника */
const getEmployee = async () => {
  const response = await adapter.getEmployee(props.employeeId);
  if (response.IsSuccess) {
    employee.value = response.Value;
  }
};
/**Получить проект */
const getProject = async () => {
  const response = await adapter.getProject(props.projectId);
  if (response.IsSuccess) {
    project.value = response.Value;
  }
};
/**
 * Получить список задач
 * @param page Страница
 */
const getTasks = async (page: PaginationRequestModel) => {
  const response = await adapter.getTasksByProjectId(
    new GetProjectTasksRequestModel({
      ProjectId: props.projectId,
      Filter: new TaskFilterModel({
        ...filter.value,
        Performers: [props.employeeId],
      }),
      Page: page,
      Sort: sortingData.value,
    }),
  );

  if (response.IsSuccess) {
    pageResponse.value = response;
  }

  return response.IsSuccess;
};
/**Получить данные */
const getData = async () => {
  isInitLoading.value = true;
  const getTasksRequest = new PaginationRequestModel({
    Skip: 0,
    Take: pageStep.value,
  });
  await Promise.all([getTasks(getTasksRequest), getEmployee(), getProject()]);
  isInitLoading.value = false;
};
/**Получить query параметры */
const getQueryParams = () => {
  const query = router.currentRoute.value.query;
  namespaceId = query.namespaceId as string | undefined;
  role = query.role as 'observer' | 'performer';
  if (typeof role !== 'string' || !['observer', 'performer'].includes(role)) {
    router.push('/');
    return;
  }
};
/**
 * Фильтрация идентификаторов для сужения типов
 * @param ids Список идентификаторов
 * @param type Тип идентификатора
 */
const filterIds = <T extends 'number' | 'string'>(
  ids: Array<string | number | undefined>,
  type: T,
) => {
  return ids.filter((id) => typeof id === type) as Array<T extends 'number' ? number : string>;
};

/**
 * При обновлении фильтра
 * @param kanbanFilter Фильтр канабан
 */
const onUpdateFilter = (kanbanFilter: TaskKanbanFilterModel) => {
  filter.value = new TaskFilterModel({
    ...filter.value,
    Statuses: filterIds(
      kanbanFilter.Status.map((_status) => _status.StatusId),
      'number',
    ),
    Priorities: filterIds(
      kanbanFilter.Priority.map((_priority) => _priority.PriorityId),
      'number',
    ),
    // TODO: Добавить остальные параметры
  });
};

/**Обновить тип сортировки */
const updateSortData = async (data: TaskSortingModel) => {
  if (!currentPageRequest.value) return;
  isLoadingSort.value = true;
  sortingData.value = new TaskSortingModel(data);
  await getTasks(currentPageRequest.value);
  isLoadingSort.value = false;
};
/**При поиске */
const onSearch = async () => {
  if (!currentPageRequest.value) return;
  await getTasks(currentPageRequest.value);
};
/**Обновить текущую страницу */
const changeCurrentPage = async (value: number) => {
  if (!pageResponse.value) return;

  await getTasks(
    new PaginationRequestModel({
      Skip: pageResponse.value.Size * (value - 1),
      Take: pageStep.value,
    }),
  );
};
/**При поиске через debounce */
const debounceOnSearch = debounce(onSearch, 100);
/**Сброс фильтра */
const resetFilter = () => {
  filter.value = new TaskFilterModel();
};
/**Изменить кол-во элементов на странице */
const changePageStep = async (value: number) => {
  pageStep.value = value;
  await getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};
// #endregion Функции
</script>

<template>
  <TitledLayout v-if="!isInitLoading">
    <template #title>
      <div class="employee-tasks-page__employee-header" v-if="employee">
        <TirPmIconView
          is-rounded
          :is-bordered="false"
          :size="24"
          :color-code="employee.AvatarColor"
          :image-load-url="employee.Photo"
        >
          <template #mainSlot>
            <span v-text="employee.AvatarInitials" />
          </template>
        </TirPmIconView>
        <span class="employee-tasks-page__employee-header-name">
          {{ employee.LastName }} {{ employee.FirstName }} {{ employee.MiddleName }}
        </span>
      </div>
    </template>
    <div class="employee-tasks-page">
      <div class="employee-tasks-page__project-info" v-if="project">
        <TirPmIconView
          is-rounded
          :is-bordered="false"
          :size="24"
          :color-code="project.PreviewColor"
          :image-load-url="project.ImageUrl"
        >
          <template #mainSlot>
            <span v-text="project.Name[0]" />
          </template>
        </TirPmIconView>
        <span class="employee-tasks-page__project-info-name">
          {{ project.Name }}
        </span>
      </div>
      <div class="employee-tasks-page__filter">
        <!-- <TasksKanbanFilter
          tasksOrderPages
          v-model:view-elements="viewSettings"
          :tasks="tasks"
          :projectId="projectId"
          @on-search="debounceOnSearch"
          @filterOptions="onUpdateFilter"
          @cancelFilter="resetFilter"
        /> -->
      </div>
      <template v-if="pageResponse?.Current && pagesCount">
        <TirPmScrollbar>
          <div class="employee-tasks-page__main">
            <TaskHeader
              class="employee-tasks-page__columns-header"
              :sort-data="sortingData"
              :is-loading="isLoadingSort"
              :columns="columns"
              :enable-sorting="true"
              :view-settings="viewSettings"
              @update:sort-data="updateSortData"
            />
            <div class="employee-tasks-page__list">
              <TaskCard
                v-for="task in tasks"
                :task="task"
                :columns="columns"
                :view-settings="viewSettings"
                :key="task.Id"
                @removeTask="
                  getTasks(
                    new PaginationRequestModel({
                      Skip: 0,
                      Take: pageStep,
                    }),
                  )
                "
              />
            </div>
          </div>
        </TirPmScrollbar>
        <TirPmPagination
          class="project-tasks__paginator"
          :model-value="pageResponse.Current"
          :range-size="3"
          :pages="pagesCount"
          :show-more-elements="true"
          :options="[10, 20, 30, 40, 50, 100]"
          :count-elements="pageStep"
          @update:count-elements="changePageStep"
          @update:model-value="changeCurrentPage"
        />
      </template>
      <div v-else class="employee-tasks-page__empty-placeholder">Задачи отсутствуют</div>
    </div>
  </TitledLayout>
</template>

<style lang="scss" scoped>
.employee-tasks-page {
  &__main {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  &__employee-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__employee-header-name {
    color: var(--text-secondary, #686c73);
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
  }
  &__project-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.875rem;
  }
  &__project-info-name {
    color: var(--text-primary, #272d37);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
  }
  &__empty-placeholder {
    color: var(--neutral-50);
  }
}
</style>
